/**
 * SHEIN 三模式静态能力矩阵（与后端 SheinShopCapabilities 对齐）。
 * 动态字段仍以 query-publish-fill-in-standard 为准。
 *
 * 文档不明项（默认策略，不强行猜测）：
 * 1. add-custom-attribute-value — 无模式标签：三模式先允许，失败再收敛
 * 2. /open-api/gsp/warehouse-address — 仅 FAQ：保留探测回退，不标正式能力
 * 3. query-sku-sales — 全托管默认开；半托管可选；自运营关
 * 4. stock-query 标签冲突 — 严格按三模式调用
 * 5. 全托管额度 — UI 用 check-publish-permission，不用 query-shelf-quota
 * 6. 半托管 site_list + cost_info — 按文档传；实机拒绝再收紧
 * 7. 自运营 shelf_way — 以发布规范动态决定
 * 8. webhook：三模式已订阅事件由后端按模式增量接收并 2xx 回包
 */
import { fixedUi } from '@/i18n/fixedUi';
import i18n from '@/i18n';
import { localizedShopTypeLabel } from '@/i18n/shopTypeText';
import { normalizeSheinShopType } from '@/utils/platformShopCatalog';

export const SHEIN_MODE_SEMI = fixedUi("半托管");
export const SHEIN_MODE_SELF = '自运营';
export const SHEIN_MODE_FULL = '全托管';

export const INVENTORY_API_V2 = 'change-inventory-v2';
export const INVENTORY_API_STOCK_UPDATE = 'stock-update';

export const FINANCE_MODE_CHECK = 'checkOrder';
export const FINANCE_MODE_REPORT = 'reportOrder';

const CAPS_BY_MODE = {
  [SHEIN_MODE_SEMI]: {
    mode: SHEIN_MODE_SEMI,
    usesCostInfo: true,
    usesPriceInfo: false,
    canUpdateCost: true,
    canUpdateSalePrice: false,
    canDiscussCost: true,
    canShelfToggle: true,
    shelfStatusViaSpuInfoOnly: false,
    usesStopPurchase: false,
    usesShelfRequireAlways: false,
    usesPublishSiteList: true,
    warehouseList: true,
    inventoryApi: INVENTORY_API_V2,
    stockQueryWarehouseType: 2,
    financeMode: FINANCE_MODE_CHECK,
    financeHasCostPrice: true,
    canReturnOrderApi: true,
    canQuerySkuSales: true,
    shelfQuotaControlled: true,
    storeNameStatusAvailable: true,
    showAfterSalesNav: true,
    showOrdersNav: true,
    showDiscussNav: true,
  },
  [SHEIN_MODE_SELF]: {
    mode: SHEIN_MODE_SELF,
    usesCostInfo: false,
    usesPriceInfo: true,
    canUpdateCost: false,
    canUpdateSalePrice: true,
    canDiscussCost: false,
    canShelfToggle: true,
    shelfStatusViaSpuInfoOnly: false,
    usesStopPurchase: false,
    usesShelfRequireAlways: false,
    usesPublishSiteList: true,
    warehouseList: true,
    inventoryApi: INVENTORY_API_V2,
    stockQueryWarehouseType: 2,
    financeMode: FINANCE_MODE_CHECK,
    financeHasCostPrice: false,
    canReturnOrderApi: true,
    canQuerySkuSales: false,
    shelfQuotaControlled: true,
    storeNameStatusAvailable: true,
    showAfterSalesNav: true,
    showOrdersNav: true,
    showDiscussNav: true,
  },
  [SHEIN_MODE_FULL]: {
    mode: SHEIN_MODE_FULL,
    usesCostInfo: true,
    usesPriceInfo: false,
    canUpdateCost: true,
    canUpdateSalePrice: false,
    canDiscussCost: true,
    canShelfToggle: false,
    shelfStatusViaSpuInfoOnly: true,
    usesStopPurchase: true,
    usesShelfRequireAlways: true,
    usesPublishSiteList: false,
    warehouseList: false,
    inventoryApi: INVENTORY_API_STOCK_UPDATE,
    stockQueryWarehouseType: 3,
    financeMode: FINANCE_MODE_REPORT,
    financeHasCostPrice: false,
    canReturnOrderApi: false,
    canQuerySkuSales: true,
    shelfQuotaControlled: false,
    storeNameStatusAvailable: false,
    showAfterSalesNav: false,
    showOrdersNav: false,
    showDiscussNav: true,
  },
};

const EMPTY_CAPS = Object.freeze({
  mode: '',
  usesCostInfo: false,
  usesPriceInfo: false,
  canUpdateCost: false,
  canUpdateSalePrice: false,
  canDiscussCost: false,
  canShelfToggle: false,
  shelfStatusViaSpuInfoOnly: true,
  usesStopPurchase: false,
  usesShelfRequireAlways: false,
  usesPublishSiteList: false,
  warehouseList: false,
  inventoryApi: INVENTORY_API_V2,
  stockQueryWarehouseType: 2,
  financeMode: FINANCE_MODE_CHECK,
  financeHasCostPrice: false,
  canReturnOrderApi: false,
  canQuerySkuSales: false,
  shelfQuotaControlled: false,
  storeNameStatusAvailable: false,
  showAfterSalesNav: false,
  showOrdersNav: false,
  showDiscussNav: false,
});

export function resolveSheinShopMode(shopType) {
  return normalizeSheinShopType(shopType) || '';
}

export function sheinShopCapabilities(shopTypeOrShop) {
  const shopType = typeof shopTypeOrShop === 'object' && shopTypeOrShop != null
    ? shopTypeOrShop.shopType
    : shopTypeOrShop;
  const mode = resolveSheinShopMode(shopType);
  if (!mode) {
    // 未知类型：返回空能力常量（勿每次 new object，避免 effect 依赖抖）
    return EMPTY_CAPS;
  }
  return CAPS_BY_MODE[mode] || CAPS_BY_MODE[SHEIN_MODE_SEMI];
}

export function workspaceSubtitleForShopType(shopType) {
  const mode = resolveSheinShopMode(shopType);
  if (mode) {
    const modeLabel = localizedShopTypeLabel(
      (key, opts) => i18n.t(key, { ns: 'workbench', ...opts }),
      mode,
    );
    return i18n.t('platformBar.subtitleMode', {
      ns: 'workbench',
      mode: modeLabel,
      defaultValue: `${modeLabel}工作区`,
    });
  }
  return i18n.t('platformBar.subtitleFallback', { ns: 'workbench', platform: 'SHEIN', defaultValue: 'SHEIN 工作区' });
}

const SELF_FALLBACK = {
  headline: '对接 SHEIN 自运营后，售价发品与站点运营更顺一截',
  lead: '把产品发布、售价改价、营销报名、证书合规与订单财务收进同一工作区——自运营按售价规则发品，可站点上下架。',
  highlights: [
    ['售价驱动发品', '自运营发品与改价走售价口径，与半托管/全托管供货价模式区分开。'],
    ['站点与上下架可控', '支持站点相关上架与 SKC 上下架；库存走商家仓能力。'],
    ['运营模块齐全', '营销活动、合规资质、模板库（含证书/实拍/建议价）、订单售后与财务账单可同步。'],
    ['店铺类型强隔离', '工作区 Session 绑定自运营；跨半托/全托需回门禁重验，与 TEMU / TikTok 工作区分离。'],
  ],
};

const FULL_FALLBACK = {
  headline: '对接 SHEIN 全托管后，供货发品与报账更顺一截',
  lead: '把供货价发品、价格处理、营销与报账单财务收进同一工作区——上下架由平台管控，状态以同步结果为准。',
  highlights: [
    ['供货价发品', '全托管按供货商模式发品（供货价、采购相关字段）；不提供商家侧上下架操作。'],
    ['价格与营销可处理', '议价单、建议零售价与营销活动报名入口在工作区内可达。'],
    ['报账单财务', '财务走全托管报账体系，与半托管/自运营对账单口径区分。'],
    ['店铺类型强隔离', '工作区 Session 绑定全托管；跨类型需回门禁，与 TEMU / TikTok 工作区分离。'],
  ],
};

const SEMI_FALLBACK = {
  headline: '对接 SHEIN 半托管后，发品与供货运营更顺一截',
  lead: '把设计产出、供货价发品、价格处理、证书合规与订单财务收进同一工作区——少切换工具，少踩规则坑。',
  highlights: [
    ['设计到上架不断层', 'IPE 主图与刊登能力直连 SHEIN 半托管：发布、产品信息、草稿与发布记录同一工作流。'],
    ['发品与价格可追踪', '供货价发品、议价/建议零售价处理、证书与合规资料闭环，状态一眼看清。'],
    ['营销订单财务齐备', '营销活动报名、订单/退货、财务对账单与店铺信息按授权店铺同步。'],
    ['店铺类型强隔离', '工作区二次验证 + 店铺类型绑定；不可串用自运营/全托管规则，亦与其他电商平台工作区分离。'],
  ],
};

function sheinStory(branch, fallback) {
  const text = (key, zh) => i18n.t(`sheinGate.${branch}.${key}`, { ns: 'workbench', defaultValue: zh });
  return {
    headline: text('headline', fallback.headline),
    lead: text('lead', fallback.lead),
    highlights: fallback.highlights.map(([title, desc], index) => ({
      title: text(`h${index + 1}Title`, title),
      desc: text(`h${index + 1}Desc`, desc),
    })),
  };
}

export function gateCopyForShopType(shopType) {
  const mode = resolveSheinShopMode(shopType) || SHEIN_MODE_SEMI;
  if (mode === SHEIN_MODE_SELF) return sheinStory('self', SELF_FALLBACK);
  if (mode === SHEIN_MODE_FULL) return sheinStory('full', FULL_FALLBACK);
  return sheinStory('semi', SEMI_FALLBACK);
}
